'use client';

import { useEffect, useState } from 'react';
import type { LiveHistoryRun, LiveSite, PlansCatalogue } from '@content-removal/shared';
import { LockIcon } from '../icons';
import { Modal, ModalShell } from '../primitives';
import { Input } from '../form';
import { PlanPicker } from '../plan-picker';
import type { PlanChoice } from '../plan-columns';
import { useToast } from '../toast';
import { Ring, lastDays } from './cases';
import { StatIcon, type OverviewLink } from './live-overview';

/** A new site, said the way the case list says it, for four seconds. */
export function FindingToasts({ fresh, onOpen }: { fresh: LiveSite[]; onOpen: (site: LiveSite) => void }) {
  const [shown, setShown] = useState<Array<{ site: LiveSite; at: number }>>([]);

  useEffect(() => {
    if (fresh.length === 0) return;
    setShown((current) => [...fresh.map((site) => ({ site, at: Date.now() })), ...current].slice(0, 3));
  }, [fresh]);

  useEffect(() => {
    if (shown.length === 0) return;
    const timer = setInterval(() => setShown((current) => current.filter((one) => Date.now() - one.at < 4200)), 500);
    return () => clearInterval(timer);
  }, [shown.length]);

  return (
    <div className="ov-toasts" aria-live="polite">
      {shown.map(({ site }) => (
        <button
          type="button"
          key={site.domain}
          className="ov-toast"
          onClick={() => {
            setShown((current) => current.filter((one) => one.site.domain !== site.domain));
            onOpen(site);
          }}
        >
          {site.locked ? <span className="ov-lock"><LockIcon /></span> : <Ring site={site} />}
          <span className="ov-toast-body">
            <span className={`ov-toast-k${site.hot ? '' : ' is-mid'}`}>
              {site.impersonation ? 'Someone is using your name' : site.hot ? 'Urgent, found your content' : 'Found your content'}
            </span>
            <span className="ov-toast-d">{site.domain}</span>
            <span className="ov-toast-m">
              {site.linkCount} {site.linkCount === 1 ? 'page' : 'pages'}
              {site.match != null ? `, ${site.match}% sure it is you` : ''}
              {site.inGoogle > 0 && <span className="chip chip-progress">In Google</span>}
              {site.locked && <span className="chip">Locked</span>}
            </span>
          </span>
          <span className="ov-toast-life" />
        </button>
      ))}
    </div>
  );
}

/** Remove all: every plan side by side, with what the pages put at risk. */
export function Paywall({
  sites,
  pages,
  inGoogle,
  riskUsd,
  kind,
  title,
  plans,
  onChoose,
  onClose,
}: {
  sites: LiveSite[];
  pages: number;
  inGoogle: number;
  riskUsd: number;
  kind: 'creator' | 'agency';
  /** Opened to choose a plan rather than to remove pages: the page count is left out, since it may be none. */
  title?: string;
  /** The live catalogue, or its failure: the prices are never written here. */
  plans: { data: PlansCatalogue | null; error: string | null };
  /** Buying, or asking sales: the app's to do. Resolves when the screen may be let go. */
  onChoose: (choice: PlanChoice) => Promise<void>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  async function choose(choice: PlanChoice) {
    setBusy(choice.plan.code);
    try {
      await onChoose(choice);
    } catch (err) {
      toast.bad((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <Modal title={title ?? (pages === 1 ? 'Remove this page' : `Remove all ${pages} pages`)} onClose={onClose} full>
      {(!title || sites.length > 0) && <div className="ov-pw-head">
        <div className="ov-faces">
          {sites.slice(0, 4).map((site) => (
            <span key={site.domain} className={site.hot ? 'is-hot' : ''}>{site.locked ? '?' : site.domain[0]}</span>
          ))}
          {sites.length > 4 && <span className="is-more">+{sites.length - 4}</span>}
        </div>
        <p>
          They are on {sites.length} {sites.length === 1 ? 'site' : 'sites'}
          {inGoogle ? `, and ${inGoogle} show up when someone searches your name` : ''}. Every plan takes them down and keeps them down.
        </p>
      </div>}
      {plans.data ? (
        <PlanPicker plans={plans.data.plans} only={kind} busy={busy} riskUsd={riskUsd} onChoose={choose} />
      ) : (
        <p className="muted">{plans.error ?? 'Loading plans…'}</p>
      )}
    </Modal>
  );
}

/** More names, accounts and photos: each one is searched from the next search on. */
export function ImproveModal({
  profileHref,
  onSave,
  onClose,
  Link,
}: {
  profileHref: string;
  /** New names and account pages, already split; the app writes them. */
  onSave: (names: string[], pages: string[]) => Promise<void>;
  onClose: () => void;
  Link: OverviewLink;
}) {
  const [names, setNames] = useState('');
  const [accounts, setAccounts] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const split = (text: string) => text.split(/[,\n]/).map((one) => one.trim()).filter(Boolean);

  async function save() {
    setBusy(true);
    try {
      await onSave(
        split(names).map((one) => one.replace(/^@/, '')),
        split(accounts).map((one) => (/^https?:\/\//.test(one) ? one : `https://${one}`)),
      );
      toast.ok('Saved. They are searched from the next search on.');
      onClose();
    } catch (err) {
      toast.bad((err as Error).message);
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Help us find more"
      sub="Every name and account you add is searched too, starting with the next search."
      onClose={onClose}
      foot={
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || (!names.trim() && !accounts.trim())} onClick={save}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Input id="improve-names" label="Other names you post under" placeholder="Mia R, miarosexo" value={names} onChange={(e) => setNames(e.target.value)} />
      <Input id="improve-accounts" label="Accounts on other sites" placeholder="fansly.com/miarose" value={accounts} onChange={(e) => setAccounts(e.target.value)} />
      <p className="muted">
        More photos make the match surer. <Link href={profileHref}>Add photos to your profile</Link>.
      </p>
    </Modal>
  );
}

/**
 * Every search we ran for her: the four totals as the Overview's own tiles,
 * fourteen days of bars, and each run as a row the way the case list draws
 * one. `historyHref` is the full Scan History page, which only the client app
 * has; the admin preview passes none and gets no footer.
 */
export function HistoryDrawer({
  history,
  historyHref,
  Link,
  onClose,
}: {
  history: LiveHistoryRun[];
  historyHref?: string;
  Link: OverviewLink;
  onClose: () => void;
}) {
  const days = lastDays(history);
  const max = Math.max(1, ...days.map((day) => day.found + day.cameBack));
  const sum = (key: 'found' | 'cameBack' | 'removed' | 'placesChecked') => history.reduce((n, run) => n + run[key], 0);
  const when = (iso: string) => new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const found = sum('found');
  const back = sum('cameBack');
  const first = history[history.length - 1];
  const last = history[0];
  return (
    <ModalShell onClose={onClose} backClassName="drawer-back" panelClassName="drawer ov-drawer" labelledBy="ov-history-title">
      <div className="ov-drawer-head">
        <span className="ov-drawer-label">Scan history</span>
        <h2 id="ov-history-title">Every search we ran for you</h2>
        {first && last && (
          <p className="ov-drawer-sub">
            {history.length === 1
              ? `One search so far, on ${when(first.startedAt)}.`
              : `${history.length} searches since ${when(first.startedAt)}, the last one ${when(last.startedAt)}.`}
          </p>
        )}
        <button type="button" className="modal-close ov-drawer-close" onClick={onClose} aria-label="Close">×</button>
      </div>
      <div className="ov-drawer-body">
        <div className="ov-dr-sum">
          <div className="ov-stat">
            <StatIcon kind="radar" />
            <b>{history.length}</b>
            <span>{history.length === 1 ? 'search' : 'searches'}</span>
          </div>
          <div className={`ov-stat${found > 0 ? ' is-hot' : ''}`}>
            <StatIcon kind="pages" />
            <b>{found}</b>
            <span>{found === 1 ? 'page found' : 'pages found'}</span>
          </div>
          <div className={`ov-stat${back > 0 ? ' is-warm' : ''}`}>
            <StatIcon kind="clock" />
            <b>{back}</b>
            <span>came back</span>
          </div>
          <div className="ov-stat">
            <StatIcon kind="sites" />
            <b>{sum('placesChecked').toLocaleString('en-US')}</b>
            <span>places checked</span>
          </div>
        </div>
        {history.length > 1 && (
          <section className="ov-dr-sec">
            <div className="ov-dr-sec-h">
              <h3>Last 14 days</h3>
              <span className="ov-dr-legend">
                <i className="is-new" /> new pages
                <i className="is-back" /> came back
              </span>
            </div>
            <div className="ov-bars">
              {days.map((day) => (
                <div key={day.key} title={`${day.key}: ${day.found} new, ${day.cameBack} came back`}>
                  {day.found + day.cameBack === 0 ? (
                    <i className="is-zero" />
                  ) : (
                    <>
                      {day.cameBack > 0 && <i className="is-back" style={{ height: `${(day.cameBack / max) * 70}px` }} />}
                      {day.found > 0 && <i className="is-new" style={{ height: `${(day.found / max) * 70}px` }} />}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="ov-bars-x"><span>14 days ago</span><span>today</span></div>
          </section>
        )}
        <section className="ov-dr-sec">
          <div className="ov-dr-sec-h">
            <h3>Each search</h3>
            <span>newest first</span>
          </div>
          <ol className="ov-runs">
            {history.map((run, index) => {
              const tone = run.found > 0 ? ' is-hot' : run.cameBack > 0 ? ' is-warm' : run.removed > 0 ? ' is-good' : '';
              return (
                <li className={`ov-run${tone}`} key={run.id}>
                  <StatIcon kind={run.found > 0 ? 'pages' : run.removed > 0 && !run.cameBack ? 'removed' : 'radar'} />
                  <div className="ov-run-l">
                    <b>{when(run.startedAt)}</b>
                    <span>
                      {index === history.length - 1 ? 'Your first search' : run.mode === 'live' ? 'You asked for a new search' : 'Scheduled search'}
                      {' · '}
                      {run.placesChecked.toLocaleString('en-US')} {run.placesChecked === 1 ? 'place' : 'places'} checked
                    </span>
                  </div>
                  <span className="ov-run-out">
                    {run.found > 0 && <span className="chip chip-danger">{run.found} new</span>}
                    {run.cameBack > 0 && <span className="chip chip-attention">{run.cameBack} came back</span>}
                    {run.removed > 0 && <span className="chip chip-resolved">{run.removed} removed</span>}
                    {!run.found && !run.cameBack && !run.removed && <span className="chip chip-progress">Nothing new</span>}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      </div>
      {historyHref && (
        <div className="ov-drawer-foot">
          <Link href={historyHref} className="ov-open">
            Open full scan history
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M9 4l4 4-4 4" /></svg>
          </Link>
        </div>
      )}
    </ModalShell>
  );
}
